import ApolloClient from 'apollo-client';
import gql from 'graphql-tag';
import { config } from '../config';

let token: string;
let issued: number;
async function getToken(client: ApolloClient<object>) {
	if (token && Date.now() - issued < 1000 * 60 * 5) {
		return token;
	}
	const {data} = await client.query<{getTableauToken: string}>({
		query: TABLEAU_TOKEN,
		fetchPolicy: 'network-only'
	});
	token = data.getTableauToken;
	issued = Date.now();
	return token;
}

export async function makeCharts(client: ApolloClient<object>, container?: HTMLDivElement) {
	const charts = container?.querySelectorAll<HTMLDivElement>('[data-id="tableau-viz"]');
	if (!charts || !charts.length) return;
	const token = await getToken(client);
	const [first, ...rest] = charts;
	// render first chart, then render the rest
	// simultaneous creation of multiple tableau-viz components leads to 10091 error, as all iframes tries to authenticate first
	// https://help.tableau.com/current/online/en-us/connected_apps_troubleshoot.htm
	renderChart(token, first, () => {
		rest.forEach(ch => renderChart(token, ch));
	});
}

export function isChartsArticle(html?: string) {
	return html ? html.indexOf('data-id="tableau-viz"') > -1 : false;
}

function renderChart(token: string, ch: HTMLDivElement, callback?: () => void) {
	const name = ch.dataset.name;
	const found = !!ch.querySelector(`tableau-viz[data-name="${name}"]`) || !!ch.querySelector(`a[data-name="${name}"]`);
	if (found) {
		return;
	}

	ch.innerHTML = '';

	const ua = navigator.userAgent;
	const isIOS = ua.match(/(iPod|iPhone|iPad)/) !== null && ua.match(/AppleWebKit/) !== null && ua.search('CriOS') < 0;
	const isSafari = ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') <= -1;
	const isAndroid = ua.match(/Android/i) !== null;

	if( isIOS || isSafari || isAndroid ) {
		// to remove tabs from this link, use `tabs:n` query param
		const embedParams = ':embed=y&:apiID=embhost0&:apiInternalVersion=1.87.0&:apiExternalVersion=3.1.0&navType=0&navSrc=Opt&:disableUrlActionsPopups=n&:toolbar=n&:device=default&mobile=n&:hideEditButton=n&:hideEditInDesktopButton=n&:suppressDefaultEditBehavior=n&:jsdebug=n';
		const target = encodeURIComponent(`/views/${name}?${embedParams}`);
		const link = `${config.tableauUrl}/vizportal/api/web/v1/auth/embed/target?token=${encodeURIComponent(token)}&target=${target}`;

		const placeholderContent = `
			<span style="font-family: 'Inter'; font-weight: 700; font-size: 18px; line-height: 24px; color: #CCCCCC;">View interactive chart</span>
			<svg width="47" height="46" viewBox="0 0 47 46" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M18.5 46C28.4411 46 36.5 37.9411 36.5 28C36.5 18.0589 28.4411 10 18.5 10C8.55887 10 0.5 18.0589 0.5 28C0.5 37.9411 8.55887 46 18.5 46Z" fill="url(#paint0_linear_18722_32635)"/>
				<path d="M18.5 27.491L41.38 44.131C44.601 39.57 46.5 34.008 46.5 28C46.5 12.536 33.964 0 18.5 0V27.491Z" fill="url(#paint1_linear_18722_32635)"/>
				<defs>
					<linearGradient id="paint0_linear_18722_32635" x1="18.5" y1="10" x2="18.5" y2="46" gradientUnits="userSpaceOnUse">
						<stop stop-color="#F77CC6"/>
						<stop offset="1" stop-color="#E642A3"/>
					</linearGradient>
					<linearGradient id="paint1_linear_18722_32635" x1="32.5" y1="0" x2="32.5" y2="44.131" gradientUnits="userSpaceOnUse">
						<stop stop-color="#FBB0DD"/>
						<stop offset="1" stop-color="#F77CC6"/>
					</linearGradient>
				</defs>
			</svg>

			<a href="${link}" data-name="${name}" target="_blank" style="text-underline: none; color: #FFFFFF; padding: 12px 24px; background: #454545; border-radius: 3px; font-family: 'Inter'; font-weight: 600; font-size: 14px; line-height: 16px; text-alighn: center;">
				Open in new tab
			</a>
		`;

		const div = document.createElement('div');
		div.style.cssText = 'display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 32px 16px; border: 2px solid #383838; border-radius: 4px;'
		div.innerHTML = placeholderContent;
		if (callback) {
			callback();
		}
		ch.appendChild(div);
	} else {
		const chart = document.createElement('tableau-viz');
		chart.setAttribute('data-name', name || '');
		// chart.setAttribute('hide-tabs', ''); // keep commented code to revert if needed, as this parameter is not that obvious from tableau docs
		chart.setAttribute('toolbar', 'hidden');
		chart.setAttribute('src', `${config.tableauUrl}/views/${name}`);
		chart.setAttribute('token', token);
		if (callback) {
			chart.addEventListener('firstinteractive', callback);
		}
		ch.appendChild(chart);
	}
}

export const TABLEAU_TOKEN = gql`query getTableauToken{getTableauToken}`;
export const TABLEAU_CHARTS = gql`query getTableauCharts{ getTableauCharts {id, title, path, image} }`;
export const TABLEAU_ENABLED = gql`query me{ me {tableauAccess} }`;